import { Camera, Info, Upload } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useGetDefaultCoverImages } from '~shared/api/cover-image/get-default-cover-images';
import { ImageCropperDialog } from '~shared/components/image-cropper-dialog';
import { useImageCropper } from '~shared/hooks/useImageCropper';
import { Button } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

interface CoverImageSelectorProps {
  onFileSelect: (file: File | string, type: 'file' | 'url') => void;
  imageUrl?: string;
  selectDefaultOnLoad?: boolean;
}

const ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];
const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MIN_COVER_WIDTH = 800;
const MIN_COVER_HEIGHT = 450;
const TARGET_ASPECT = 16 / 9;

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
}

function getExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
}

function isAcceptedImage(file: File): boolean {
  const mimeOk = ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase());
  const extOk = ACCEPTED_EXTENSIONS.includes(getExtension(file.name));
  return mimeOk || extOk;
}

function getMax16x9CropDimensions(
  width: number,
  height: number,
): { width: number; height: number } {
  const imageAspect = width / height;
  if (imageAspect >= TARGET_ASPECT) {
    return { width: height * TARGET_ASPECT, height };
  }
  return { width, height: width / TARGET_ASPECT };
}

function loadImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.width, height: image.height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    image.src = url;
  });
}

export const CoverImageSelector = ({
  onFileSelect,
  imageUrl,
  selectDefaultOnLoad = false,
}: CoverImageSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [selectedCover, setSelectedCover] = useState<string>(imageUrl ?? '');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    if (imageUrl) {
      setSelectedCover(imageUrl);
    }
  }, [imageUrl]);

  const { data: defaultCoverImages } = useGetDefaultCoverImages();
  const defaultCoverUrl = defaultCoverImages?.[0]?.url;

  useEffect(() => {
    if (!imageUrl && selectDefaultOnLoad && defaultCoverUrl && !selectedCover) {
      setSelectedCover(defaultCoverUrl);
      onFileSelect(defaultCoverUrl, 'url');
    }
  }, [
    defaultCoverUrl,
    imageUrl,
    onFileSelect,
    selectDefaultOnLoad,
    selectedCover,
  ]);

  const handleCropCompleteCallback = useCallback(
    (croppedFile: File, croppedImageUrl: string) => {
      onFileSelect(croppedFile, 'file');
      setSelectedCover(croppedImageUrl);
      setOpen(false);
    },
    [onFileSelect, setSelectedCover],
  );

  const {
    cropperOpen,
    cropperImageSrc,
    handleImageChange: handleCropperImageChange,
    handleCropComplete,
    setCropperOpen,
  } = useImageCropper({
    onCropComplete: handleCropCompleteCallback,
  });

  const validateImage = (file: File): string | null => {
    if (!isAcceptedImage(file)) {
      return 'Please upload a JPG, PNG, or WebP image.';
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `Image must be smaller than 5 MB. Yours is ${formatFileSize(
        file.size,
      )}.`;
    }

    return null;
  };

  const handleFileUpload = async (file: File) => {
    setError(null);
    setWarning(null);

    const validationError = validateImage(file);
    if (validationError) {
      setError(validationError);
      setOpen(false);
      return;
    }

    try {
      const { width, height } = await loadImageDimensions(file);
      const maxCrop = getMax16x9CropDimensions(width, height);
      if (
        maxCrop.width < MIN_COVER_WIDTH ||
        maxCrop.height < MIN_COVER_HEIGHT
      ) {
        setWarning(
          `For best results, use a 16:9 image at least ${MIN_COVER_WIDTH} x ${MIN_COVER_HEIGHT} px.`,
        );
      }
    } catch {
      // If dimension checks fail, let the cropper handle the file.
    }

    setOpen(false);
    try {
      handleCropperImageChange(file);
    } catch (error) {
      console.error('Failed to handle image:', error);
    }
  };

  const selectDefaultCover = (coverUrl: string) => {
    setError(null);
    setWarning(null);
    onFileSelect(coverUrl, 'url');
    setSelectedCover(coverUrl);
    setOpen(false);
  };

  return (
    <>
      <div className='relative mb-8 w-full'>
        {/* Current Cover Display */}
        <div
          className='aspect-video cursor-pointer overflow-hidden rounded-lg bg-gray-100'
          onClick={() => {
            setIsFullScreen(true);
          }}
        >
          <img
            src={selectedCover || defaultCoverImages?.[0]?.url}
            alt='Cover'
            className='aspect-video size-full object-cover'
          />
        </div>

        {/* Change Cover Button */}
        <div className='absolute bottom-4 right-4'>
          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant='secondary'>
                <Camera />
                Change Cover
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='end'
              side='bottom'
              sideOffset={4}
              className='grid w-[600px] grid-cols-5 gap-2 p-2'
            >
              {defaultCoverImages?.map((cover) => (
                <div
                  key={cover._id}
                  onClick={() => {
                    selectDefaultCover(cover.url);
                  }}
                  className='relative aspect-video cursor-pointer overflow-hidden rounded-md hover:ring-2 hover:ring-primary'
                >
                  <img src={cover.url} className='size-full object-cover' />
                </div>
              ))}
              <label className='flex aspect-video cursor-pointer items-center justify-center rounded-md border-2 border-dashed hover:bg-gray-50'>
                <input
                  type='file'
                  accept='image/jpeg, image/png, image/jpg, image/webp'
                  className='hidden'
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      void handleFileUpload(file);
                    }
                    e.target.value = '';
                  }}
                />
                <Upload className='size-6 text-gray-400' />
              </label>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className='-mt-6 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:-mt-7'>
        <span>JPG, PNG, WebP</span>
        <span aria-hidden='true'>&bull;</span>
        <span>16:9</span>
        <span aria-hidden='true'>&bull;</span>
        <span>1600 x 900+ ideal</span>
        <span aria-hidden='true'>&bull;</span>
        <span>5 MB max</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                className='inline-flex items-center rounded-full p-0.5 hover:bg-accent'
                aria-label='Image upload help'
              >
                <Info className='size-3.5' />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side='right'
              className='max-w-xs text-xs'
            >
              <p>
                Cover images display as 16:9 cards. For crisp results, use an
                sRGB image that is at least 800 x 450 px (1600 x 900 px ideal)
                and under 5 MB.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {error && (
        <p className='-mt-4 mb-6 text-sm text-destructive' role='alert'>
          {error}
        </p>
      )}

      {warning && !error && (
        <p className='-mt-4 mb-6 text-sm text-amber-600' role='status'>
          {warning}
        </p>
      )}

      {/* Full Screen Preview */}
      {isFullScreen && (
        <div
          className='fixed inset-0 z-50 flex cursor-pointer items-center justify-center bg-black/80'
          onClick={() => {
            setIsFullScreen(false);
          }}
        >
          <img
            src={selectedCover || defaultCoverImages?.[0]?.url}
            alt='Cover'
            className='max-h-[90vh] max-w-[90vw] object-contain'
          />
        </div>
      )}

      {cropperImageSrc && cropperOpen && (
        <ImageCropperDialog
          open={cropperOpen}
          onOpenChange={setCropperOpen}
          imageSrc={cropperImageSrc}
          onCropComplete={handleCropComplete}
          aspectRatio={16 / 9}
          title='Crop Cover Image'
          description='Adjust your cover image to the desired crop area'
        />
      )}
    </>
  );
};
